/**
 * Document Builder service layer.
 *
 * A "document" is the unified proposal + contract + invoice + questionnaire
 * the studio sends to a client as one signable URL. It's composed of an
 * ordered list of typed blocks (heading, paragraph, signature, lineitem,
 * etc.) so the studio can mix-and-match content per project.
 *
 * Status flow:
 *   draft  → studio is authoring
 *   sent   → public link generated; recipient hasn't opened yet
 *   viewed → recipient hit the URL; first_viewed_at populated
 *   signed → all signature blocks completed; signed_at populated
 *   paid   → (future) Stripe Connect payment captured
 *   expired / void — terminal states
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrgId } from '@/hooks/useOrgId';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoStore } from '@/contexts/DemoStoreContext';

// ─── Types ───────────────────────────────────────────────────────

export type DocumentStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'signed'
  | 'paid'
  | 'expired'
  | 'void';

/// One signer entry in `OrbitDocument.signers`. The studio adds
/// these on the document setup page; the public sign view filters
/// blocks by `role` so each signer only fills their own. Status
/// flips to 'signed' on the document only when every entry's
/// `signed_at` is non-null.
export interface DocumentSigner {
  /// Stable role key. 'primary' is the canonical default; 'cosigner'
  /// is the most common second signer; the studio can set anything
  /// else (e.g. 'witness', 'spouse') for free-form labels.
  role: string;
  name: string;
  email: string | null;
  /// ISO8601 set when this signer completes their blocks.
  /// null means they haven't signed yet.
  signed_at: string | null;
}

export interface OrbitDocument {
  id: string;
  org_id: string;
  project_id: string | null;
  client_id: string | null;
  title: string;
  status: DocumentStatus;
  /// Payment lifecycle, separate from `status`. Status follows the
  /// signing flow (draft→sent→signed); payment_status follows the
  /// money flow (unpaid→pending→paid→refunded). Both can be set.
  payment_status?: 'unpaid' | 'pending' | 'paid' | 'refunded';
  public_token: string;
  is_template: boolean;
  project_type: string | null;
  total_cents: number;
  sent_at: string | null;
  first_viewed_at: string | null;
  signed_at: string | null;
  paid_at: string | null;
  expires_at: string | null;
  /// Multi-signer routing (DocuSign-style). One element per signer;
  /// each signature/initials block carries a matching `signerRole`
  /// field that decides which signer fills it. Backwards compatible —
  /// docs created before the multi-signer feature get a 1-element
  /// array via the 20260524_documents_multi_signer.sql backfill.
  signers: DocumentSigner[];
  /// CONTRACT-IOS-1 — what the document is: 'proposal' | 'contract' |
  /// 'invoice' | 'questionnaire', or 'mixed' (the column default) when
  /// nobody said. Contracts made in the Contract Studio are 'contract'.
  kind?: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/// Discriminated-union type for block payloads. Add new variants
/// here as block types are introduced — the runtime is just JSON
/// so older clients tolerate unknown types gracefully.
///
/// `signerRole` (signature + initials blocks): which signer in
/// `documents.signers[]` is responsible for this block. Defaults
/// to 'primary' for backwards compatibility — single-signer docs
/// don't need to set it. Multi-signer docs assign each block
/// either 'primary', 'cosigner', or any custom role string the
/// studio adds to the signers array.
export type DocumentBlockPayload =
  | { type: 'heading'; text: string; level?: 1 | 2 | 3 }
  | { type: 'paragraph'; text: string }
  | { type: 'divider' }
  | { type: 'image'; url: string; caption?: string }
  | { type: 'signature'; signerLabel?: string; signerRole?: string; required?: boolean }
  /// Initials field — used to mark every clause the signer
  /// confirms inline (DocuSign-style). Same canvas mechanism as
  /// `signature`, just rendered smaller in the public sign view
  /// and intended to be repeated multiple times in a doc. The
  /// signer captures their initials once and they get re-used
  /// for every initials block on submit.
  | { type: 'initials'; signerLabel?: string; signerRole?: string; required?: boolean }
  | { type: 'lineitem'; name: string; description?: string; price_cents: number; quantity?: number }
  | { type: 'package'; options: { id: string; name: string; price_cents: number; description?: string }[]; allowMultiple?: boolean }
  | { type: 'deposit'; percentage?: number; fixed_cents?: number; label?: string }
  | { type: 'terms'; text: string }
  /**
   * INVOICE-8 — a link to one of the studio's intake forms.
   *
   * Rennan: *"being able to also link them to one of your forms."* A
   * BLOCK rather than a column on `documents`, because that is what
   * it is: content on the document, which the client sees and acts
   * on, like the terms above it. It also needs no migration — the
   * payload is JSON and this file's own note says older clients
   * tolerate unknown block types.
   *
   * The id AND the resolved name and URL are all stored, which is
   * not what I first wrote here. Resolving at render time would be
   * better — a renamed form would not leave a stale label behind —
   * but the page this renders on is ANONYMOUS, and reading
   * `intake_forms` by id as anon is not possible past the SEC-1
   * lockdown: the public path needs an org slug and a form slug and
   * goes through `public-org-lookup`. So the label and the url are a
   * snapshot taken when the invoice was saved.
   *
   * `form_id` is kept anyway, so a later version with a public
   * by-id lookup can re-resolve and the snapshot becomes a fallback
   * rather than the only truth.
   */
  | { type: 'form_link'; form_id: string; label?: string; url?: string }
  | { type: 'questionnaire'; fields: { id: string; label: string; placeholder?: string; required?: boolean }[] };

export interface DocumentBlock {
  id: string;
  document_id: string;
  sort_order: number;
  type: DocumentBlockPayload['type'];
  payload: DocumentBlockPayload;
  created_at: string;
  updated_at: string;
}

export interface DocumentSignature {
  id: string;
  document_id: string;
  block_id: string | null;
  signer_name: string;
  signer_email: string | null;
  signature_data: string;
  ip_address: string | null;
  user_agent: string | null;
  signed_at: string;
}

// ─── Documents: list / get / create / update / delete ────────────

/// All documents for a project (e.g. workspace tab). Includes templates
/// only if `includeTemplates` is true (templates have project_id null).
export const useProjectDocuments = (projectId: string | undefined) => {
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useQuery({
    queryKey: ['documents', 'project', projectId, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      if (!projectId) return [] as OrbitDocument[];
      if (isDemo) {
        // Session-scoped overlay first — non-template docs only.
        return demoStore.documents
          .list()
          .filter((d) => d.project_id === projectId && d.is_template === false) as unknown as OrbitDocument[];
      }
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('project_id', projectId)
        .eq('is_template', false)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as OrbitDocument[];
    },
    enabled: !!projectId,
  });
};

/// All templates for the org (for the "new from template" picker).
export const useDocumentTemplates = () => {
  const { data: orgId } = useOrgId();
  return useQuery({
    queryKey: ['documents', 'templates', orgId],
    queryFn: async () => {
      if (!orgId) return [] as OrbitDocument[];
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('org_id', orgId)
        .eq('is_template', true)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as OrbitDocument[];
    },
    enabled: !!orgId,
  });
};

export const useDocument = (documentId: string | undefined) => {
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useQuery({
    queryKey: ['document', documentId, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      if (!documentId) return null;
      if (isDemo) {
        const row = demoStore.documents.list().find((d) => d.id === documentId);
        return (row ?? null) as unknown as OrbitDocument | null;
      }
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('id', documentId)
        .single();
      if (error) throw error;
      return data as unknown as OrbitDocument;
    },
    enabled: !!documentId,
  });
};

/// Create a blank document. Use `applyTemplate()` after if seeding
/// from a template.
export const useCreateDocument = () => {
  const queryClient = useQueryClient();
  const { data: orgId } = useOrgId();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useMutation({
    mutationFn: async ({
      projectId,
      clientId,
      title,
      isTemplate,
      projectType,
      kind,
    }: {
      projectId?: string | null;
      clientId?: string | null;
      title?: string;
      isTemplate?: boolean;
      projectType?: string | null;
      /// CONTRACT-IOS-1 — 'contract' from the Contract Studio.
      kind?: 'proposal' | 'contract' | 'invoice' | 'questionnaire';
    }) => {
      if (isDemo) {
        const now = new Date().toISOString();
        const id = `demo-doc-${crypto.randomUUID().slice(0, 8)}`;
        const created = demoStore.documents.add({
          id,
          org_id: 'demo-org',
          project_id: projectId ?? null,
          client_id: clientId ?? null,
          title: title ?? 'Untitled document',
          status: 'draft',
          kind: kind ?? 'document',
          payment_status: 'unpaid',
          public_token: `demo-token-${id}`,
          is_template: !!isTemplate,
          project_type: projectType ?? null,
          total_cents: 0,
          sent_at: null,
          first_viewed_at: null,
          signed_at: null,
          paid_at: null,
          expires_at: null,
          signers: [{ role: 'primary', name: 'Primary signer', email: null, signed_at: null }],
          created_by: 'demo-studio-user',
          created_at: now,
          updated_at: now,
        });
        return created as unknown as OrbitDocument;
      }

      if (!orgId) throw new Error('Your studio isn’t set up yet');
      const { data, error } = await supabase
        .from('documents')
        .insert({
          org_id: orgId,
          project_id: projectId ?? null,
          client_id: clientId ?? null,
          title: title ?? 'Untitled document',
          is_template: !!isTemplate,
          project_type: projectType ?? null,
          ...(kind ? { kind } : {}),
        })
        .select()
        .single();
      if (error) throw error;
      return data as unknown as OrbitDocument;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
};

export const useUpdateDocument = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<OrbitDocument> & { id: string }) => {
      if (isDemo) {
        demoStore.documents.update(id, updates as Record<string, any>);
        return;
      }
      const { error } = await supabase
        .from('documents')
        .update({ ...updates, updated_at: new Date().toISOString() } as any)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['document'] });
    },
  });
};

export const useDeleteDocument = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useMutation({
    mutationFn: async (id: string) => {
      if (isDemo) {
        demoStore.documents.remove(id);
        return;
      }
      const { error } = await supabase.from('documents').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
};

/// Mark the document as sent — generates the audit timestamp + flips
/// status so the public RLS policy lets the recipient view it.
export const useSendDocument = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useMutation({
    mutationFn: async ({ id, clientId }: { id: string; clientId?: string | null }) => {
      if (isDemo) {
        const now = new Date().toISOString();
        demoStore.documents.update(id, {
          status: 'sent',
          sent_at: now,
          client_id: clientId ?? null,
          updated_at: now,
        });
        return;
      }
      const { error } = await supabase
        .from('documents')
        .update({
          status: 'sent',
          sent_at: new Date().toISOString(),
          client_id: clientId ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['document'] });
    },
  });
};

// ─── Blocks: list / add / update / reorder / delete ──────────────

export const useDocumentBlocks = (documentId: string | undefined) => {
  return useQuery({
    queryKey: ['document-blocks', documentId],
    queryFn: async () => {
      if (!documentId) return [] as DocumentBlock[];
      const { data, error } = await supabase
        .from('document_blocks')
        .select('*')
        .eq('document_id', documentId)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as DocumentBlock[];
    },
    enabled: !!documentId,
  });
};

export const useAddBlock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      documentId,
      payload,
      insertAt,
    }: {
      documentId: string;
      payload: DocumentBlockPayload;
      insertAt?: number;
    }) => {
      // Append to end if no position given.
      const { data: existing } = await supabase
        .from('document_blocks')
        .select('sort_order')
        .eq('document_id', documentId)
        .order('sort_order', { ascending: false })
        .limit(1);
      const nextOrder =
        insertAt !== undefined ? insertAt : (existing?.[0]?.sort_order ?? -1) + 1;

      const { data, error } = await supabase
        .from('document_blocks')
        .insert({
          document_id: documentId,
          sort_order: nextOrder,
          type: payload.type,
          payload,
        })
        .select()
        .single();
      if (error) throw error;
      return data as DocumentBlock;
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['document-blocks', vars.documentId] });
    },
  });
};

export const useUpdateBlock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      documentId,
      payload,
    }: {
      id: string;
      documentId: string;
      payload: DocumentBlockPayload;
    }) => {
      const { error } = await supabase
        .from('document_blocks')
        .update({
          payload,
          type: payload.type,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['document-blocks', vars.documentId] });
    },
  });
};

export const useDeleteBlock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, documentId }: { id: string; documentId: string }) => {
      const { error } = await supabase.from('document_blocks').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['document-blocks', vars.documentId] });
    },
  });
};

// ─── Public: read by token + sign ────────────────────────────────

/// Fetch a document by its public_token — works without auth. Used
/// by the /d/:token client view.
///
/// SEC-1 lockdown: the anon SELECT policies on documents /
/// document_blocks / document_signatures are gone (they exposed
/// every org's public_token + captured IPs). One RPC now returns
/// the whole { document, blocks, signatures } bundle — the document
/// minus public_token, signatures minus ip_address/user_agent.
export const useDocumentByToken = (token: string | undefined) => {
  return useQuery({
    queryKey: ['document-by-token', token],
    queryFn: async () => {
      if (!token) return null;
      // Cast through `any` — types.ts isn't regenerated for the
      // SEC-1 RPCs yet (same pattern as deliveryService).
      const { data, error } = await (supabase as any).rpc('get_public_document_by_token', {
        p_token: token,
      });
      if (error) throw error;
      // Null when the token is wrong or the doc isn't in a public
      // status — matches the old .single() error path for the page.
      if (!data) throw new Error('Document not found');

      return {
        document: data.document as unknown as OrbitDocument,
        blocks: (data.blocks ?? []) as DocumentBlock[],
        signatures: (data.signatures ?? []) as DocumentSignature[],
      };
    },
    enabled: !!token,
  });
};

/**
 * Studio-side fetch of every signature on a document.
 *
 * Pulls the document_signatures rows the studio is authorized
 * to see (via RLS) for the audit trail panel in DocumentEditor.
 * Each row carries: signer name + email, signed_at timestamp,
 * captured ip_address (inet), user_agent string, and the
 * base64 signature_data PNG. The audit UI renders all of that
 * verbatim — these rows are the legal record so we surface
 * exactly what's stored.
 *
 * Append-only: there's no `useUpdateSignature` or
 * `useDeleteSignature` and there shouldn't be — the table's
 * RLS allows no UPDATE/DELETE for studios for the same reason.
 */
export const useDocumentSignatures = (documentId: string | undefined) => {
  return useQuery({
    queryKey: ['document-signatures', documentId],
    queryFn: async () => {
      if (!documentId) return [] as DocumentSignature[];
      const { data, error } = await supabase
        .from('document_signatures')
        .select('*')
        .eq('document_id', documentId)
        .order('signed_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as DocumentSignature[];
    },
    enabled: !!documentId,
  });
};

/// Submit signatures from the client view. Inserts one row per
/// signature block (skipped entirely when the doc has no signature
/// blocks — e.g. a read-only acknowledgement), then updates the
/// signers array + flips status to "signed" iff every signer has
/// completed.
///
/// Multi-signer flow:
///   - If `signerRole` is omitted (legacy single-signer), the doc
///     flips straight to 'signed' as before.
///   - If `signerRole` is present, only that signer's `signed_at`
///     gets stamped. The doc flips to 'signed' only when EVERY
///     entry in `signers` has a non-null `signed_at`. Until then
///     it stays in 'viewed' so the next signer can still open it.
export const useSubmitSignatures = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      token,
      signatures,
      signerRole,
    }: {
      /// The document's public_token from the /d/:token URL — the
      /// credential the RPC keys on (SEC-1: the client no longer
      /// writes document_signatures / documents directly).
      token: string;
      signatures: {
        block_id: string | null;
        signer_name: string;
        signer_email?: string | null;
        signature_data: string;
      }[];
      /// Which signer in `documents.signers[]` is submitting. Omit
      /// for legacy single-signer documents (treated as 'primary').
      signerRole?: string;
    }) => {
      // SEC-1 lockdown: one RPC replaces the signature insert + the
      // signers-array read/update. Server-side it skips empty
      // signature_data rows, stamps this signer's signed_at, and
      // flips to 'signed' only when everyone has signed — same
      // multi-signer semantics as before.
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
      // Cast through `any` — types.ts isn't regenerated for the
      // SEC-1 RPCs yet (same pattern as deliveryService).
      const { error } = await (supabase as any).rpc('public_document_submit_signatures', {
        p_token: token,
        p_signatures: signatures.map((s) => ({
          block_id: s.block_id,
          signer_name: s.signer_name,
          signer_email: s.signer_email ?? null,
          signature_data: s.signature_data,
        })),
        p_signer_role: signerRole ?? null,
        p_user_agent: userAgent,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-by-token'] });
      queryClient.invalidateQueries({ queryKey: ['document'] });
    },
  });
};

/// Mark the document as viewed (first time the recipient opens it).
/// Idempotent — the RPC only fires when status is 'sent' and
/// first_viewed_at is unset.
///
/// SEC-1 lockdown: takes the public token instead of the document
/// id — anon can no longer select/update documents directly.
export const markDocumentViewed = async (token: string) => {
  // Cast through `any` — types.ts isn't regenerated for the
  // SEC-1 RPCs yet (same pattern as deliveryService).
  await (supabase as any).rpc('public_document_mark_viewed', { p_token: token });
};

// ─── Apply a template to create a new document ───────────────────

/// Clone a template's blocks into a fresh draft document attached
/// to a project. The new document is editable; the template is left
/// alone for re-use.
export const useApplyTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      templateId,
      projectId,
      clientId,
      title,
    }: {
      templateId: string;
      projectId: string;
      clientId?: string | null;
      title?: string;
    }) => {
      // Fetch template + blocks
      const { data: template, error: tplError } = await supabase
        .from('documents')
        .select('*')
        .eq('id', templateId)
        .single();
      if (tplError) throw tplError;

      const { data: blocks } = await supabase
        .from('document_blocks')
        .select('*')
        .eq('document_id', templateId)
        .order('sort_order', { ascending: true });

      // Create new document
      const { data: created, error: createError } = await supabase
        .from('documents')
        .insert({
          org_id: template.org_id,
          project_id: projectId,
          client_id: clientId ?? null,
          title: title ?? template.title,
          is_template: false,
          project_type: template.project_type,
        })
        .select()
        .single();
      if (createError) throw createError;

      // Clone blocks
      if (blocks && blocks.length > 0) {
        const cloned = blocks.map((b) => ({
          document_id: created.id,
          sort_order: b.sort_order,
          type: b.type,
          payload: b.payload,
        }));
        await supabase.from('document_blocks').insert(cloned);
      }

      return created as unknown as OrbitDocument;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
};
